import {
    put, select, takeLatest, delay
} from 'redux-saga/effects';
import { createModule } from 'saga-slice';
import {
    failReducer,
    loadingReducer,
    notLoadingReducer,
    noop
} from 'saga-slice-helpers';

import history from '#/history';
import storage from '#/storage';
import {
    addAuthorization,
    removeAuthorization,
    sagaApi
} from '#/apis';
import { wait } from '#/meta';
import { flashAction } from '+/flashes/redux';


const lsAuth = storage.get('auth');

const initialState = {
    isSignedIn: false,
    token: null,
    role: null,
    authorization: null,
    settings: {},
    expires: null,
    id: null,
    isFetching: false,
    error: null,
    isCheckingSessions: false,
    permissions: {}
};

if (lsAuth) {

    addAuthorization(lsAuth.authorization);
}

const sagaSliceModule = createModule({
    name: 'auth',
    initialState: {
        ...initialState,
        ...(lsAuth || {}),
        isSignedIn: !!lsAuth
    },
    reducers: {

        loginSuccess: (state, payload) => {

            Object.assign(state, {
                ...payload,
                isFetching: false,
                isSignedIn: true,
                error: null
            });


            addAuthorization(payload.authorization);
        },

        logoutDone: state => {
            Object.keys(state).forEach(key => {

                if (initialState.hasOwnProperty(key)) {

                    state[key] = initialState[key];
                }
                else {

                    delete state[key];
                }
            });
        },

        logout: loadingReducer,
        logoutSuccess: notLoadingReducer,

        login: loadingReducer,

        loginFail: failReducer,
        loginDone: noop,

        logoutFail: failReducer,

        register: noop,
        registerSuccess: noop,
        registerFail: noop,
        registerDone: noop,

        confirm: noop,
        confirmSuccess: noop,
        confirmFail: noop,
        confirmDone: noop,

        requestReset: loadingReducer,
        requestResetSuccess: notLoadingReducer,
        requestResetFail: failReducer,
        requestResetDone: noop,

        resetPassword: loadingReducer,
        resetPasswordSuccess: notLoadingReducer,
        resetPasswordFail: failReducer,

        updatePassword: loadingReducer,
        updatePasswordSuccess: (state, payload) => {
            state = initialState;
        },
        updatePasswordFail: failReducer,

        setCheckingSessions: (state, checking) => {
            state.isCheckingSessions = checking;
        },
        assertSession: noop,
        checkSession: noop,
        checkSessionSuccess: noop,
        checkSessionFail: noop,
        checkSessionDone: noop
    },
    sagas: A => ({

        [A.resetPassword]: {
            *saga({ payload }) {
                yield sagaApi.put('/auth/password/reset', payload, A.resetPasswordSuccess, A.resetPasswordFail);

            }
        },

        [A.updatePassword]: {
            *saga({ payload }) {
                yield sagaApi.put('/auth/update-password', payload, A.updatePasswordSuccess, A.updatePasswordFail, A.logoutDone);
            }
        },

        [A.login]: {
            *saga ({ payload }) {

                yield wait(1000);
                yield sagaApi.put('/auth', payload, A.loginSuccess, A.loginFail, A.loginDone);
            }
        },
        [A.loginDone]: {
            *saga({ payload: { data } }) {

                if (data) {

                    storage.set('auth', data);
                    yield history.push('/');
                }

                yield;
            }
        },


        [A.logout]: {
            *saga () {

                yield wait(1000);
                yield sagaApi.delete('/auth', null, A.logoutSuccess, A.logoutFail, A.logoutDone);
            }
        },
        [A.logoutDone]: {
            *saga() {

                removeAuthorization();
                storage.rmv('auth');
                yield put(A.setCheckingSessions(false));
                yield history.push('/login');
            }
        },

        [A.register]: {
            *saga({ payload }) {
                yield sagaApi.post('/auth', payload, A.registerSuccess, A.registerFail, A.registerDone);
            }
        },
        [A.registerDone]: {
            *saga({ payload }) {

                if (payload.data) {

                    yield history.push('/login');
                    yield put(flashAction({
                        type: 'success',
                        permanent: true,
                        message: 'Successfully created an account! Please check your email and confirm your account.'
                    }));
                }

                yield;
            }
        },

        [A.confirm]: {
            *saga({ payload: token }) {

                yield sagaApi.get(`/auth/confirm/${token}`, A.loginSuccess, A.confirmFail, A.loginDone);
            }
        },
        [A.confirmFail]: {
            *saga({ payload }) {

                const isSignedIn = yield select(state => state.auth.isSignedIn);

                if (isSignedIn) {

                    history.push('/');
                }
                else {

                    history.push('/login');
                }

                // Wait for rerender
                yield wait(250);
                yield put(flashAction({
                    type: 'error',
                    message: payload.response.data.message
                }));
            },
            taker: takeLatest
        },
        [A.requestReset]: {
            *saga({ payload }) {

                yield sagaApi.post('/auth/password/reset', payload, A.requestResetSuccess, A.requestResetFail, A.requestResetDone);

            }
        },
        [A.requestResetDone]: {
            *saga({ payload }) {

                if (payload.data) {
                    yield history.push('/login');
                    yield delay(1000);
                    yield put(flashAction({
                        type: 'success',
                        permanent: true,
                        message: 'Please check your email to reset your password.'
                    }));

                }

                yield;
            }
        },

        [A.checkSession]: {
            *saga() {

                yield sagaApi.get('/auth/session', A.checkSessionSuccess, A.logoutDone, A.checkSessionDone);
            },
            taker: takeLatest
        },

        /**
         * Asserts that session is valid.
         * Checks session every 60 seconds.
         * Checks if session is expired.
         * If any case is true, trigger a logou
         */
        [A.assertSession]: {
            *saga() {

                const isCheckingSessions = yield select(state => state.auth.isCheckingSessions);
                if (isCheckingSessions) {

                    return;
                }

                yield put(A.setCheckingSessions(true));

                while (true) {

                    yield wait(1000);

                    const {
                        isSignedIn,
                        expires
                    } = yield select(state => state.auth);

                    if (!isSignedIn) {
                        break;
                    }

                    if ((+new Date()) - expires < 0) {

                        put(A.logout());
                        break;
                    }

                    yield put(A.checkSession());
                    yield wait(1000 * 60 * 10);
                }
            }
        }
    })
});

export const { actions } = sagaSliceModule;
export default sagaSliceModule;
